import { z } from "zod";
import { Accession, Cik, IsoDate, Timestamp } from "./primitives.js";
import { AskScope } from "./ask.js";
import { IndexKey } from "./index_membership.js";
/** Every kind of work the queue carries; each has one payload shape in `JobPayload`. */
export const JobKind = z.enum([
    "scan_daily_index",
    "fetch_filing",
    "extract_text",
    "retriage_filing",
    "build_tables",
    "recount_images",
    "classify_candidate",
    "reconcile_filing",
    "embed_document",
    "fetch_company_facts",
    "extract_terms",
    "deep_extract",
    "evaluate_flags",
    "backfill_window",
    "backfill_terms",
    "deliver_notification",
    "refresh_company_tickers",
    "fetch_prices",
    "fetch_index_holdings",
    "ask_whole",
    "place_filing",
]);
/**
 * Where a job is in the queue. `failed` is terminal; a failure that will be retried returns
 * to `pending`.
 */
export const JobState = z.enum(["pending", "running", "done", "failed"]);
/** Payload shape per job kind, so a malformed enqueue fails at the boundary. */
export const JobPayload = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("scan_daily_index"), date: IsoDate }),
    z.object({ kind: z.literal("fetch_filing"), accession: Accession, cik: Cik }),
    z.object({ kind: z.literal("extract_text"), accession: Accession }),
    /**
     * Re-run the current Screen over text that is already stored. Fetches nothing.
     *
     * One filing per job, so a pass over the corpus does not hold one worker throughout
     * and leaves its progress durable if it dies partway.
     */
    z.object({ kind: z.literal("retriage_filing"), accession: Accession }),
    /**
     * Reconstruct one document's table grids from bytes already on disk.
     *
     * Fetches nothing. One document per job, for `retriage_filing`'s reason.
     */
    z.object({ kind: z.literal("build_tables"), documentId: z.string().uuid() }),
    z.object({ kind: z.literal("recount_images"), documentId: z.string().uuid() }),
    z.object({ kind: z.literal("classify_candidate"), accession: Accession }),
    z.object({ kind: z.literal("reconcile_filing"), accession: Accession }),
    z.object({ kind: z.literal("embed_document"), documentId: z.string().uuid() }),
    z.object({
        kind: z.literal("fetch_company_facts"),
        cik: Cik,
        /**
         * Refetch even when the cached copy is fresh. `data.sec.gov` sends neither `ETag`
         * nor `Last-Modified` for this endpoint, so the cache's age is the only guard.
         */
        force: z.boolean().optional(),
    }),
    /**
     * The headline-terms pass: local, touching no network, and answering a subset of
     * `deep_extract`'s question against the same filing.
     */
    z.object({ kind: z.literal("extract_terms"), accession: Accession, situationId: z.string().uuid() }),
    /**
     * A deep read, answered by a Claude Code session rather than the worker.
     *
     * `scope` decides whether a windowed read may stop early, and defaults to `full`. A
     * `terms` read stops once the ratio, both dates and the SpinCo's name are settled,
     * which an information statement states first. It can move a stage but not fill a
     * situation's facts: everything it skipped looks like something the document does not
     * contain.
     */
    z.object({
        kind: z.literal("deep_extract"),
        accession: Accession,
        situationId: z.string().uuid(),
        scope: z.enum(["full", "terms"]).optional(),
    }),
    z.object({ kind: z.literal("evaluate_flags"), situationId: z.string().uuid() }),
    z.object({ kind: z.literal("backfill_window"), from: IsoDate, to: IsoDate }),
    /**
     * Fetch the 8-Ks around ONE situation's registration from the submissions history of
     * the companies already on it.
     *
     * A Form 10 can leave its record and distribution dates blank in every version filed;
     * the parent announces them in an 8-K. `backfill_window` walks only Form 10s, so for a
     * backfilled situation this is how those 8-Ks arrive. One situation per job, for
     * `retriage_filing`'s reason.
     */
    z.object({ kind: z.literal("backfill_terms"), situationId: z.string().uuid() }),
    /**
     * Which of a parent's several separations this filing is about: the question
     * reconciliation cannot answer when a filer has two live situations that both match on
     * CIK. No situation id, because the filing has none yet.
     *
     * Its own kind rather than part of `deep_extract`: `extraction_runs` is keyed on the
     * hash of the exact prompt, and a placement prompt carries the candidate situations,
     * which change whenever any situation for the company does. Folded in, that would
     * invalidate the stored extraction too.
     *
     * Like `deep_extract` and `ask_whole`, the worker has no handler for it: it is answered
     * by a Claude Code session through `/api/extractions`.
     */
    z.object({ kind: z.literal("place_filing"), accession: Accession }),
    z.object({ kind: z.literal("deliver_notification"), notificationId: z.string().uuid() }),
    z.object({ kind: z.literal("refresh_company_tickers") }),
    /**
     * Refresh the price for one ticker.
     *
     * One ticker per job, for `retriage_filing`'s reason, and so the dedupe key can mean
     * one refresh per ticker per day.
     */
    z.object({ kind: z.literal("fetch_prices"), ticker: z.string().min(1) }),
    /**
     * Refresh one index's holdings from the fund that tracks it.
     *
     * An NPORT-P covers a quarter and a re-fetch inside one replaces the same rows, so the
     * dedupe key is the index and the period.
     */
    z.object({ kind: z.literal("fetch_index_holdings"), index: IndexKey }),
    /**
     * A `whole`-depth question, answered by a Claude Code session rather than the API key.
     *
     * The scope travels in the payload because `ask_threads` does not store one.
     */
    z.object({
        kind: z.literal("ask_whole"),
        threadId: z.string().uuid(),
        messageId: z.string().uuid(),
        scope: AskScope,
    }),
]);
/** One queued unit of work, with its retry and lock bookkeeping. */
export const Job = z.object({
    id: z.string().uuid(),
    kind: JobKind,
    payload: JobPayload,
    state: JobState,
    /** Higher is claimed first; see `JOB_PRIORITY`. */
    priority: z.number().int(),
    runAfter: Timestamp,
    attempts: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    lastError: z.string().nullable(),
    lockedBy: z.string().nullable(),
    lockedAt: Timestamp.nullable(),
    createdAt: Timestamp,
});
/**
 * Job priorities; higher is claimed first.
 *
 * Later pipeline stages outrank earlier ones, so the queue drains depth-first and a
 * situation surfaces as soon as the filing that evidences it has been read, rather than
 * after the whole day's ingestion.
 */
export const JOB_PRIORITY = {
    interactive: 10,
    reconcile: 6,
    scan: 5,
    extract: 4,
    fetch: 0,
    /**
     * Classification sits above backfill, not below it: under a continuous backfill,
     * anything below it is never claimed, and a starved annotation looks identical to one
     * that ran and found nothing. Below everything that moves a filing toward a situation,
     * because it never gates one.
     */
    classify: -1,
    /**
     * The terms pass sits beside classification, for the same two reasons.
     */
    terms: -1,
    /**
     * The company's XBRL history is a background enrichment no situation waits on; it
     * sits beside classification for the same two reasons.
     */
    companyFacts: -1,
    /**
     * Rebuilding table grids: nothing waits on it, since a document without grids reads as
     * text. At backfill, so it yields to everything; the work is bounded by the corpus.
     */
    tables: -2,
    /**
     * Counting a document's images sits with `tables`, for the same reason: the count is a
     * diagnostic, and a document without one reads and searches exactly as well.
     */
    recountImages: -2,
    backfill: -2,
};
//# sourceMappingURL=job.js.map